import { useCallback, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { DatePicker } from "./DatePicker";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { UploadSimple, FilePdf, FileCsv, FileXls, Trash, Sparkle, X } from "@phosphor-icons/react";
import { STATUSES, PRIORITIES } from "../lib/constants";
import { toast } from "sonner";

/**
 * PDF / XLSX / CSV → procurement items importer.
 *
 * Two-step flow:
 *  1. Upload → POST /projects/{id}/import/parse — Claude extracts rows.
 *  2. Review grid → user edits, excludes bad rows, remaps contractors.
 *  3. Save → POST /projects/{id}/import/commit.
 *
 * When `defaultContractorId` is passed (standalone-contractor use case),
 * every parsed row is pre-scoped to that bucket and the contractor picker
 * column is hidden.
 */
export default function ImportScheduleDialog({
  projectId,
  contractors,
  onDone,
  defaultContractorId = null,
  triggerButton,
}) {
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | parsing | review | saving
  const [rows, setRows] = useState([]);
  const [detected, setDetected] = useState([]);
  const [fileName, setFileName] = useState("");
  const inputRef = useRef(null);

  const reset = useCallback(() => {
    setPhase("idle");
    setRows([]);
    setDetected([]);
    setFileName("");
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const handleOpenChange = (v) => {
    setOpen(v);
    if (!v) reset();
  };

  const handleFile = async (file) => {
    if (!file) return;
    const okExts = [".pdf", ".xlsx", ".xlsm", ".csv"];
    const lower = file.name.toLowerCase();
    if (!okExts.some((e) => lower.endsWith(e))) {
      toast.error("Only PDF, Excel (.xlsx) and CSV files are supported.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large — max 8 MB.");
      return;
    }
    setFileName(file.name);
    setPhase("parsing");
    let jobId;
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Kick off a background job on the server. Response comes back in
      // <1 s with a job id — dodges any Cloudflare / ingress upstream
      // timeout because we never hold the HTTP request open through the
      // LLM call.
      const { data } = await api.post(`/projects/${projectId}/import/parse`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000, // just for the upload itself
      });
      jobId = data.job_id;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not upload the file.");
      reset();
      return;
    }

    // Poll the job status. Total budget: ~3 minutes at 2 s intervals.
    const started = Date.now();
    const POLL_MS = 2000;
    const MAX_MS = 180_000;
    while (true) {
      await new Promise((r) => setTimeout(r, POLL_MS));
      let job;
      try {
        const r = await api.get(`/projects/${projectId}/import/jobs/${jobId}`);
        job = r.data;
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Lost track of the import job.");
        reset();
        return;
      }
      if (job.status === "done") {
        const items = job.result?.items || [];
        const withDefaults = items.map((r) => ({
          ...r,
          include: true,
          // Stable React key — Claude rows have no id, and index-as-key
          // shuffles DOM whenever a row above is deleted.
          __key: (typeof crypto !== "undefined" && crypto.randomUUID)
            ? crypto.randomUUID()
            : `row-${Math.random().toString(36).slice(2)}`,
          contractor_account_id:
            defaultContractorId || r.contractor_account_id || (contractors[0]?.id ?? null),
        }));
        setRows(withDefaults);
        setDetected(job.result?.detected_contractors || []);
        setPhase("review");
        if (withDefaults.length === 0) {
          toast.warning("No procurement items were detected in this file.");
        }
        return;
      }
      if (job.status === "error") {
        toast.error(job.error || "Extraction failed.");
        reset();
        return;
      }
      if (Date.now() - started > MAX_MS) {
        toast.error("Import took too long. Please try a smaller file or split it.");
        reset();
        return;
      }
    }
  };

  const updateRow = (idx, patch) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const removeRow = (idx) => {
    setRows((rs) => rs.filter((_, i) => i !== idx));
  };

  const toggleInclude = (idx) => {
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, include: !r.include } : r)));
  };

  const includedCount = rows.filter((r) => r.include).length;
  const missingContractor = rows.some((r) => r.include && !r.contractor_account_id);

  const handleSave = async () => {
    if (includedCount === 0) {
      toast.error("Select at least one row to save.");
      return;
    }
    if (missingContractor) {
      toast.error("Every included row needs a contractor.");
      return;
    }
    setPhase("saving");
    try {
      const payload = {
        source_filename: fileName,
        items: rows
          .filter((r) => r.include)
          .map((r) => ({
            contractor_account_id: r.contractor_account_id,
            name: r.name,
            description: r.description || "",
            quantity: Number(r.quantity) || 1,
            unit: r.unit || "pcs",
            priority: r.priority || "medium",
            status: r.status || "awaiting_approval",
            eta: r.eta || null,
            notes: r.notes || "",
            location: r.location || "",
            custom_fields: r.custom_fields || {},
          })),
      };
      const { data } = await api.post(`/projects/${projectId}/import/commit`, payload);
      toast.success(`Imported ${data.created} procurement item(s).`);
      setOpen(false);
      reset();
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed.");
      setPhase("review");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {triggerButton || (
          <Button
            variant="outline"
            className="rounded-full text-sm font-semibold px-4 h-10 border-[#001E44]/20 hover:bg-[#001E44]/5"
            data-testid="import-schedule-button"
          >
            <Sparkle size={14} weight="bold" className="mr-2 text-[#007BFF]" />
            Import from file
          </Button>
        )}
      </DialogTrigger>
      <DialogContent
        className={phase === "review" ? "sm:max-w-6xl max-h-[90vh] overflow-hidden p-0 flex flex-col" : "sm:max-w-lg"}
        data-testid="import-schedule-dialog"
      >
        {phase === "review" ? (
          <ReviewGrid
            rows={rows}
            contractors={contractors}
            detected={detected}
            fileName={fileName}
            defaultContractorId={defaultContractorId}
            onUpdateRow={updateRow}
            onRemoveRow={removeRow}
            onToggleInclude={toggleInclude}
            onCancel={() => handleOpenChange(false)}
            onSave={handleSave}
            saving={phase === "saving"}
            includedCount={includedCount}
            missingContractor={missingContractor}
          />
        ) : (
          <UploadPane
            phase={phase}
            fileName={fileName}
            inputRef={inputRef}
            onFile={handleFile}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Upload pane ----------------
function UploadPane({ phase, fileName, inputRef, onFile }) {
  const [dragging, setDragging] = useState(false);
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display font-bold tracking-tight text-[#001E44]">
          Import procurement schedule
        </DialogTitle>
        <p className="text-xs text-zinc-500 mt-1 leading-snug">
          Drop a PDF, Excel, or CSV. Claude Sonnet 4.5 extracts every line item — you
          double-check on the next screen before saving.
        </p>
      </DialogHeader>
      <div
        className={`mt-4 border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          dragging ? "border-[#007BFF] bg-[#007BFF]/5" : "border-zinc-300 bg-zinc-50"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const f = e.dataTransfer.files?.[0];
          if (f) onFile(f);
        }}
        data-testid="import-dropzone"
      >
        <div className="flex items-center justify-center gap-3 mb-3">
          <FilePdf size={28} weight="light" className="text-red-500" />
          <FileXls size={28} weight="light" className="text-emerald-600" />
          <FileCsv size={28} weight="light" className="text-blue-600" />
        </div>
        {phase === "parsing" ? (
          <div className="space-y-2">
            <p className="font-display font-bold text-[#001E44]">
              Reading &ldquo;{fileName}&rdquo;…
            </p>
            <div className="flex items-center justify-center gap-1 text-xs text-zinc-500">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#007BFF] animate-pulse" />
              <span>Claude is extracting line items in the background — can take up to a couple of minutes for big PDFs.</span>
            </div>
            <p className="text-[10px] text-zinc-400 mt-2">
              Safe to keep this dialog open — polling every 2 s.
            </p>
          </div>
        ) : (
          <>
            <p className="font-display font-bold text-[#001E44]">
              Drop your file here
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              or click below · PDF, Excel or CSV · up to 8 MB
            </p>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.xlsx,.xlsm,.csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onFile(f);
              }}
              data-testid="import-file-input"
            />
            <Button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="mt-4 bg-[#007BFF] hover:bg-[#0062cc] rounded-full text-xs font-semibold px-5 uppercase tracking-wider"
              data-testid="import-choose-file-button"
            >
              <UploadSimple size={13} weight="bold" className="mr-2" />
              Choose file
            </Button>
          </>
        )}
      </div>
    </>
  );
}

// ---------------- Review grid ----------------
function ReviewGrid({
  rows, contractors, detected, fileName, defaultContractorId,
  onUpdateRow, onRemoveRow, onToggleInclude, onCancel, onSave,
  saving, includedCount, missingContractor,
}) {
  const showContractorCol = !defaultContractorId;
  return (
    <>
      <div className="px-6 pt-6 pb-4 border-b border-zinc-200 shrink-0">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-bold tracking-tight text-[#001E44]">
              Review &amp; confirm
            </h2>
            <p className="text-xs text-zinc-500 mt-1">
              Extracted from <span className="font-semibold text-[#001E44]">{fileName}</span> ·
              Fix any dates or contractors before saving.
              {detected.length > 0 && (
                <span className="ml-1">
                  Detected: {detected.slice(0, 4).join(", ")}
                  {detected.length > 4 ? "…" : ""}
                </span>
              )}
            </p>
          </div>
          <span
            className="chip bg-[#007BFF]/10 text-[#007BFF] shrink-0"
            data-testid="import-included-count"
          >
            {includedCount} of {rows.length} selected
          </span>
        </div>
      </div>
      <div className="flex-1 overflow-auto" data-testid="import-review-grid">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="p-2 w-8"></th>
              <th className="p-2 text-left min-w-[220px]">Item</th>
              <th className="p-2 text-left min-w-[90px]">Qty</th>
              <th className="p-2 text-left min-w-[80px]">Unit</th>
              {showContractorCol && (
                <th className="p-2 text-left min-w-[170px]">Contractor</th>
              )}
              <th className="p-2 text-left min-w-[130px]">Priority</th>
              <th className="p-2 text-left min-w-[160px]">Status</th>
              <th className="p-2 text-left min-w-[140px]">ETA</th>
              <th className="p-2 text-left min-w-[220px]">Notes</th>
              <th className="p-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.__key || `row-${i}`}
                className={`border-b border-zinc-100 ${
                  r.include ? "" : "opacity-40 bg-zinc-50/50"
                }`}
                data-testid={`import-row-${i}`}
              >
                <td className="p-2 text-center">
                  <input
                    type="checkbox"
                    checked={r.include}
                    onChange={() => onToggleInclude(i)}
                    className="w-4 h-4 rounded"
                    data-testid={`import-row-include-${i}`}
                  />
                </td>
                <td className="p-2">
                  <Input
                    value={r.name}
                    onChange={(e) => onUpdateRow(i, { name: e.target.value })}
                    className="h-8 text-xs"
                    data-testid={`import-row-name-${i}`}
                  />
                  {Object.keys(r.custom_fields || {}).length > 0 && (
                    <div className="text-[10px] text-zinc-400 mt-1">
                      +{Object.keys(r.custom_fields).length} extra field
                      {Object.keys(r.custom_fields).length > 1 ? "s" : ""} will be saved
                    </div>
                  )}
                </td>
                <td className="p-2">
                  <Input
                    type="number" min="0" step="0.01"
                    value={r.quantity}
                    onChange={(e) => onUpdateRow(i, { quantity: e.target.value })}
                    className="h-8 text-xs w-full"
                  />
                </td>
                <td className="p-2">
                  <Input
                    value={r.unit || ""}
                    onChange={(e) => onUpdateRow(i, { unit: e.target.value })}
                    className="h-8 text-xs w-full"
                  />
                </td>
                {showContractorCol && (
                  <td className="p-2">
                    <Select
                      value={r.contractor_account_id || ""}
                      onValueChange={(v) => onUpdateRow(i, { contractor_account_id: v })}
                    >
                      <SelectTrigger
                        className={`h-8 text-xs ${
                          r.include && !r.contractor_account_id ? "border-red-400" : ""
                        }`}
                        data-testid={`import-row-contractor-${i}`}
                      >
                        <SelectValue placeholder="Pick contractor" />
                      </SelectTrigger>
                      <SelectContent>
                        {contractors.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                )}
                <td className="p-2">
                  <Select
                    value={r.priority}
                    onValueChange={(v) => onUpdateRow(i, { priority: v })}
                  >
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-2">
                  <Select
                    value={r.status}
                    onValueChange={(v) => onUpdateRow(i, { status: v })}
                  >
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-2">
                  <DatePicker
                    value={r.eta ? r.eta.slice(0, 10) : ""}
                    onChange={(v) => onUpdateRow(i, { eta: v || null })}
                    className="h-8 text-xs w-full"
                    data-testid={`import-row-eta-${i}`}
                    placeholder="—"
                    compact
                  />
                </td>
                <td className="p-2">
                  <Input
                    value={r.notes || ""}
                    onChange={(e) => onUpdateRow(i, { notes: e.target.value })}
                    className="h-8 text-xs w-full"
                  />
                </td>
                <td className="p-2">
                  <button
                    type="button"
                    onClick={() => onRemoveRow(i)}
                    className="p-1 rounded text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                    title="Delete this row"
                    data-testid={`import-row-delete-${i}`}
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={showContractorCol ? 10 : 9} className="p-8 text-center text-zinc-500 text-sm">
                  Nothing to review — the file didn&apos;t look like a procurement schedule.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <DialogFooter className="px-6 py-4 border-t border-zinc-200 shrink-0 flex-row items-center justify-between gap-3">
        <div className="text-[11px] text-zinc-500">
          {missingContractor && (
            <span className="text-red-600 font-semibold">
              Every included row needs a contractor before saving.
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={saving}
            className="rounded-full h-9 text-xs uppercase tracking-wide font-semibold"
            data-testid="import-cancel-button"
          >
            <X size={12} weight="bold" className="mr-1.5" />
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSave}
            disabled={saving || includedCount === 0 || missingContractor}
            className="bg-[#007BFF] hover:bg-[#0062cc] rounded-full h-9 text-xs uppercase tracking-wide font-semibold text-white disabled:opacity-40"
            data-testid="import-save-button"
          >
            {saving ? "Saving…" : `Save ${includedCount} item${includedCount === 1 ? "" : "s"}`}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
